import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  ShieldCheck,
  FilePlus2,
  Wrench,
  Users,
  GraduationCap,
  ArrowRight,
  CheckCircle2,
  CalendarClock,
  RefreshCw,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Role options displayed before login. Picking a role stores the "intended role"
// so AuthCallback can route the user to the correct interface after Google login.
// NOTE: Only a non-sensitive UI preference (role key + target path) is persisted
// in localStorage. No auth tokens, no PII. The real session cookie is httpOnly
// and set by the backend at /api/auth/session. RBAC on the backend is the source
// of truth — the intended_target is only honored if user.role permits it.
const ROLES = [
  {
    key: 'admin',
    label: 'VGWS Admin',
    description: 'Chief Administrative Officer — full system, analytics, and escalation controls.',
    icon: ShieldCheck,
    accent: 'from-indigo-500 to-indigo-600',
    ring: 'ring-indigo-300',
    target: '/cao',
  },
  {
    key: 'creator',
    label: 'Staff Ticket Creator',
    description: 'Raise and track facility tickets for your department.',
    icon: FilePlus2,
    accent: 'from-[#E97832] to-[#EA580C]',
    ring: 'ring-orange-300',
    target: '/dashboard',
  },
  {
    key: 'resolver',
    label: 'Staff Ticket Resolver',
    description: 'Accept, update, and resolve tickets allocated to you.',
    icon: Wrench,
    accent: 'from-emerald-500 to-teal-600',
    ring: 'ring-emerald-300',
    target: '/process-owner',
  },
  {
    key: 'parent',
    label: 'Parent',
    description: 'Raise concerns for your child and follow up in real time.',
    icon: Users,
    accent: 'from-sky-500 to-blue-600',
    ring: 'ring-sky-300',
    target: '/dashboard',
  },
  {
    key: 'student',
    label: 'Student',
    description: 'Report hostel, campus, or academic issues from your phone.',
    icon: GraduationCap,
    accent: 'from-fuchsia-500 to-pink-600',
    ring: 'ring-fuchsia-300',
    target: '/dashboard',
  },
];

const Landing = () => {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCacheDialog, setShowCacheDialog] = useState(false);
  const [pendingRole, setPendingRole] = useState(null);
  const [isClearing, setIsClearing] = useState(false);

  // Show the "Clear cache before login" prompt at most once per browser/day so
  // we don't pester returning users on every visit but DO catch upgrade days
  // where stale service-worker caches could break login.
  useEffect(() => {
    try {
      const lastPrompted = parseInt(localStorage.getItem('vgws_cache_prompt_at') || '0', 10);
      const ONE_DAY = 24 * 60 * 60 * 1000;
      if (!lastPrompted || Date.now() - lastPrompted > ONE_DAY) {
        // Only auto-prompt if a service worker is registered (i.e., installable PWA)
        if ('serviceWorker' in navigator) {
          // Defer the dialog ~1.2s so it doesn't compete with the hero animation.
          const t = setTimeout(() => setShowCacheDialog(true), 1200);
          return () => clearTimeout(t);
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: cache-prompt scheduling failed', err?.message);
      }
    }
    return undefined;
  }, []);

  // Best-effort site-data clear: caches, IndexedDB, localStorage UI keys,
  // service-worker, then a hard reload to fetch fresh JS bundles. We
  // intentionally PRESERVE the auth cookie (httpOnly, can't be touched from JS
  // anyway) so the user isn't forced to re-OAuth unless their session has
  // genuinely expired.
  const clearSiteCacheAndReload = async () => {
    setIsClearing(true);
    try {
      // 1. CacheStorage (service-worker caches + runtime caches)
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      // 2. Service worker — unregister so the next load fetches fresh assets.
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      // 3. IndexedDB — best effort (some browsers don't expose .databases())
      if (window.indexedDB && typeof window.indexedDB.databases === 'function') {
        try {
          const dbs = await window.indexedDB.databases();
          await Promise.all(
            (dbs || []).map(
              (db) => new Promise((resolve) => {
                if (!db?.name) return resolve();
                const req = window.indexedDB.deleteDatabase(db.name);
                req.onsuccess = req.onerror = req.onblocked = () => resolve();
              }),
            ),
          );
        } catch (idbErr) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('Landing: IndexedDB clear failed', idbErr?.message);
          }
        }
      }
      // 4. Local UI prefs (NOT the session cookie which we cannot touch).
      try {
        const KEEP = ['vgws_intended_role', 'vgws_intended_target'];
        const snapshot = {};
        KEEP.forEach((k) => {
          const v = localStorage.getItem(k);
          if (v !== null) snapshot[k] = v;
        });
        localStorage.clear();
        Object.entries(snapshot).forEach(([k, v]) => localStorage.setItem(k, v));
        sessionStorage.clear();
      } catch (storageErr) {
        // Safari Private mode / quota-exceeded throws on storage writes — non-fatal.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Landing: storage clear failed', storageErr?.message);
        }
      }
      // Mark prompted so we don't ask again today.
      try { localStorage.setItem('vgws_cache_prompt_at', String(Date.now())); } catch (storageErr) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Landing: prompt-flag write failed', storageErr?.message);
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: site-cache clear failed', err?.message);
      }
    } finally {
      // Hard reload bypasses HTTP cache for index.html so users get the latest JS.
      window.location.reload();
    }
  };

  const dismissCacheDialog = () => {
    setShowCacheDialog(false);
    try {
      localStorage.setItem('vgws_cache_prompt_at', String(Date.now()));
    } catch (storageErr) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: prompt-flag write failed', storageErr?.message);
      }
    }
    if (pendingRole) {
      // User chose "Skip" while a role was waiting — continue the OAuth flow.
      const role = pendingRole;
      setPendingRole(null);
      // eslint-disable-next-line no-use-before-define
      persistIntendedRole(role);
      // eslint-disable-next-line no-use-before-define
      redirectToGoogle();
    }
  };

  // Returns the cached authenticated user if the session is still valid, else null.
  const getActiveUser = () => {
    try {
      const raw = localStorage.getItem('vgws_user');
      if (!raw) return null;
      const cached = JSON.parse(raw);
      if (cached?.session_expires && cached.session_expires > Date.now()) {
        return cached;
      }
      return null;
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: getActiveUser failed', err?.message);
      }
      return null;
    }
  };

  // Map of which roles are allowed to visit which targets.
  const ALLOWED_TARGETS = {
    cao: ['/cao', '/dashboard'],
    process_owner: ['/process-owner', '/dashboard'],
    parent_staff: ['/dashboard'],
    student: ['/dashboard'],
  };

  const persistIntendedRole = (role) => {
    try {
      // SECURITY NOTE: These two keys store ONLY a non-sensitive UI preference —
      // a role key ('parent' / 'student' / 'creator' / …) and a target path
      // ('/dashboard' / '/cao' / …). NO auth tokens, NO PII, NO session data
      // is ever written to localStorage from this app. Authentication lives in
      // an httpOnly cookie set by the backend at /api/auth/session. RBAC is
      // enforced server-side; 'vgws_intended_role' is treated as an untrusted
      // hint and only honored when server-returned user.role permits it.
      localStorage.setItem('vgws_intended_role', role.key);
      localStorage.setItem('vgws_intended_target', role.target);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: localStorage.setItem failed', err?.message);
      }
    }
  };

  const redirectToGoogle = () => {
    setIsAuthenticating(true);
    try {
      // Non-sensitive UI timestamp used to detect stuck sign-in flows.
      // localStorage can legitimately throw in Safari private-mode or when
      // storage is full — non-fatal, we continue to Google regardless.
      localStorage.setItem('login_initiated', Date.now().toString());
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Landing: localStorage.setItem(login_initiated) failed:', error?.message);
      }
    }
    setTimeout(() => {
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }, 100);
  };

  // Role card click: ALWAYS route through Google sign-in so the user can pick
  // their preferred Google account. The existing session (if any) is
  // intentionally NOT honored here — Emergent Auth will transparently continue
  // without re-prompting when the user picks the same Google account.
  const handleSelectRole = (role) => {
    setSelectedRole(role);
    // If the cache-clear dialog is currently visible, hold the role until the
    // user has answered. Prevents Google redirect from happening behind the
    // overlay.
    if (showCacheDialog) {
      setPendingRole(role);
      return;
    }
    persistIntendedRole(role);
    redirectToGoogle();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ─── Cache & Cookies Clear Prompt ────────────────────────────────── */}
      <Dialog open={showCacheDialog} onOpenChange={(open) => { if (!open) dismissCacheDialog(); }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="cache-clear-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-[#E97832]" />
              Refresh Care Desk for the smoothest experience?
            </DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              We periodically push improvements to Care Desk. Clearing this device's
              cached files and old service-worker data ensures you always see the
              latest version, avoid stale data, and prevent rare login glitches.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1">
            <p><strong>What this does:</strong></p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Clears the browser's site cache for caredesk.mitgurukul.com</li>
              <li>Resets the offline service worker (will re-install on next visit)</li>
              <li>Empties old IndexedDB & sessionStorage entries</li>
              <li>Keeps your saved role preference; does <strong>not</strong> sign you out</li>
            </ul>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={dismissCacheDialog}
              disabled={isClearing}
              data-testid="cache-clear-skip-btn"
            >
              Not now
            </Button>
            <Button
              onClick={clearSiteCacheAndReload}
              disabled={isClearing}
              className="bg-[#E97832] hover:bg-[#C25E20] text-white"
              data-testid="cache-clear-confirm-btn"
            >
              {isClearing ? 'Clearing…' : 'Clear & Reload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAuthenticating && (
        <div className="fixed inset-0 z-50 bg-white/95 backdrop-blur-sm flex items-center justify-center" data-testid="auth-loading-overlay">
          <div className="text-center space-y-4 px-4">
            <div className="w-16 h-16 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin mx-auto" />
            <h2 className="text-xl font-semibold text-slate-800">Authenticating...</h2>
            <p className="text-sm text-slate-600">Redirecting to Google Sign-In</p>
            <p className="text-xs text-slate-400">Please wait, do not refresh the page</p>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 min-h-screen">
        {/* Left hero panel — kept from previous design, narrower */}
        <div
          className="relative hidden lg:block lg:col-span-4 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1730106469498-a916bbf203e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzY2hvb2wlMjBidWlsZGluZyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzQyNDk1MDd8MA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 to-slate-800/80 backdrop-blur-sm" />
          <div className="relative h-full flex flex-col justify-between p-10 xl:p-12">
            <img
              src="https://customer-assets.emergentagent.com/job_gurukul-care-desk/artifacts/kp1n4cqn_VGWS-NEW%20LOGO%20%281%29.png"
              alt="VGWS Logo"
              className="h-16 xl:h-20 object-contain"
            />
            <div className="space-y-5 text-white">
              <h1 className="text-4xl xl:text-5xl font-heading font-bold leading-tight">Care Desk</h1>
              <p className="text-base xl:text-lg text-slate-200 max-w-md">
                Zero-latency facility management for a world-class institution. Track, resolve, and optimize every concern in real-time.
              </p>
              <div className="flex items-center gap-2 text-[#E97832] font-mono text-xs xl:text-sm">
                <Building2 className="w-5 h-5" />
                <span>Vishwashanti Gurukul World School</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right content panel */}
        <div className="lg:col-span-8 flex items-start justify-center p-4 sm:p-6 lg:p-10 overflow-y-auto">
          <div className="w-full max-w-4xl space-y-6 sm:space-y-8 py-4 sm:py-6">
            {/* Mobile header */}
            <div className="lg:hidden text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_gurukul-care-desk/artifacts/kp1n4cqn_VGWS-NEW%20LOGO%20%281%29.png"
                alt="VGWS Logo"
                className="h-12 sm:h-14 object-contain mx-auto mb-2"
              />
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Care Desk</h1>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">Vishwashanti Gurukul World School</p>
            </div>

            {/* Working-hours / holiday notice — visible on all screens */}
            <div
              data-testid="holiday-notice-banner"
              className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3 sm:p-4 shadow-sm"
              role="note"
            >
              <div className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <CalendarClock className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-wide text-amber-800">
                  Please note
                </p>
                <p className="text-xs sm:text-sm text-amber-900 mt-0.5 leading-relaxed">
                  VGWS staff hours: 8:00 AM – 6:00 PM, Monday to Friday. Tickets raised on Sundays, non-working days, or outside working hours will be processed on the next working day.
                </p>
              </div>
            </div>

            {/* Role selector section */}
            <section data-testid="role-selector-section" className="space-y-3 sm:space-y-4">
              <div className="text-center lg:text-left">
                <h2 className="text-xl sm:text-2xl font-heading font-bold text-slate-900">Choose your role to continue</h2>
                <p className="text-xs sm:text-sm text-slate-600 mt-1">
                  Tap a card to open its workspace. You&apos;ll sign in with your Google account if needed.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {ROLES.map((role) => {
                  const Icon = role.icon;
                  const isSelected = selectedRole?.key === role.key;
                  return (
                    <button
                      type="button"
                      key={role.key}
                      data-testid={`role-card-${role.key}`}
                      onClick={() => handleSelectRole(role)}
                      className={`group relative text-left bg-white border rounded-xl p-4 sm:p-5 transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                        isSelected
                          ? `border-transparent ring-2 ${role.ring} shadow-lg`
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-gradient-to-br ${role.accent} text-white flex items-center justify-center flex-shrink-0 shadow-sm`}
                        >
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-heading font-semibold text-sm sm:text-base text-slate-900 truncate">
                              {role.label}
                            </p>
                            {isSelected && (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-slate-600 mt-1 line-clamp-2">{role.description}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span
                          className={`text-[11px] sm:text-xs font-medium ${
                            isSelected ? 'text-slate-800' : 'text-slate-500 group-hover:text-slate-700'
                          }`}
                        >
                          {isSelected ? 'Opening…' : 'Continue with Google'}
                        </span>
                        <ArrowRight
                          className={`w-4 h-4 transition-transform ${
                            isSelected ? 'translate-x-0.5 text-slate-800' : 'text-slate-400 group-hover:translate-x-0.5'
                          }`}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Staff email domain reminder (kept, compact) */}
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3" data-testid="login-note-block">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-red-700 font-bold text-xs uppercase tracking-wide">
                  Staff must login with your VGWS school official mail ID —{' '}
                  <span className="bg-red-200 px-1 rounded">@mitgurukul.com</span>
                </p>
              </div>
            </div>

            <div className="text-center text-xs sm:text-sm text-slate-500">
              <p>
                Need help? Contact{' '}
                {/* Opens Gmail compose in a new tab pre-addressed to
                    jahnavi.m@mitgurukul.com (the diversion target per the
                    Apr 28, 2026 spec), with support@mitgurukul.com CC'd as
                    an audit trail. Gmail compose is preferred over a plain
                    `mailto:` here because every Care Desk visitor is on a
                    VGWS Google Workspace account, so Gmail will be open in
                    the same browser session and the email is naturally
                    sent FROM the user's own VGWS email id without needing
                    any desktop mail client to be configured.

                    For the rare visitor without an active Gmail session
                    (incognito, third-party browser) we still fall back to
                    a `mailto:` via onClick when the popup is blocked. */}
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=jahnavi.m%40mitgurukul.com&cc=support%40mitgurukul.com&su=VGWS%20Care%20Desk%20Support%20Request&body=Hi%20Care%20Desk%20Support%2C%0A%0APlease%20describe%20your%20concern%20here%3A%0A%0A"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    // If the popup is blocked (e.g., browser-extension), fall
                    // back transparently to the OS mail handler so the user
                    // never hits a dead click.
                    const popup = window.open(e.currentTarget.href, '_blank', 'noopener,noreferrer');
                    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                      window.location.href = 'mailto:jahnavi.m@mitgurukul.com?cc=support@mitgurukul.com&subject=VGWS%20Care%20Desk%20Support%20Request&body=Hi%20Care%20Desk%20Support%2C%0A%0APlease%20describe%20your%20concern%20here%3A%0A%0A';
                    }
                    e.preventDefault();
                  }}
                  className="text-[#E97832] font-medium underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-[#E97832] rounded-sm"
                  data-testid="support-email-link"
                  aria-label="Contact VGWS Care Desk support — opens Gmail compose with your VGWS email"
                >
                  support@mitgurukul.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;
